import type { SystemLog, SystemLogUpdate } from '#shared/types/custom.types'
import type { Database } from '#shared/types/database.types'

// Type definitions based on the database schema
export type LogLevel = Database['public']['Enums']['log_level']
export type LogCategory = Database['public']['Enums']['log_category']

export interface CreateLogParams {
  level: LogLevel
  category: LogCategory
  message: string
  details?: Record<string, unknown>
  error?: Error | null
  metadata?: Record<string, unknown>
}

export interface LogFilters {
  level?: LogLevel
  category?: LogCategory
  is_resolved?: boolean
  user_id?: string
  search?: string
  start_date?: string
  end_date?: string
}

export interface LogStatistics {
  total_logs: number
  error_count: number
  warning_count: number
  info_count: number
  resolved_count: number
  unresolved_count: number
  by_category: Record<string, number>
}

/**
 * Type guard to safely extract user ID from Supabase User object
 * Handles both id and sub properties for compatibility
 */
function getUserId(user: unknown): string | null {
  if (!user)
    return null
  if (typeof user === 'object' && 'id' in user && typeof user.id === 'string')
    return user.id
  if (typeof user === 'object' && 'sub' in user && typeof user.sub === 'string')
    return user.sub

  return null
}

/**
 * Composable for managing system logs
 */
export const useSupabaseLogs = () => {
  const supabase = useSupabaseClient<Database>()
  const authStore = useAuthStore()
  const user = computed(() => authStore.user)
  const loading = ref(false)
  const error = ref<Error | null>(null)

  /**
   * Get browser information
   */
  const getBrowserInfo = () => {
    if (typeof window === 'undefined')
      return {}

    const ua = navigator.userAgent
    let browser = 'Unknown'
    let os = 'Unknown'
    let device = 'Desktop'

    // Detect browser
    if (ua.includes('Firefox/'))
      browser = 'Firefox'
    else if (ua.includes('Chrome/'))
      browser = 'Chrome'
    else if (ua.includes('Safari/') && !ua.includes('Chrome/'))
      browser = 'Safari'
    else if (ua.includes('Edge/'))
      browser = 'Edge'
    else if (ua.includes('Opera/') || ua.includes('OPR/'))
      browser = 'Opera'

    // Detect OS
    if (ua.includes('Windows'))
      os = 'Windows'
    else if (ua.includes('Mac'))
      os = 'macOS'
    else if (ua.includes('Linux'))
      os = 'Linux'
    else if (ua.includes('Android'))
      os = 'Android'
    else if (ua.includes('iOS'))
      os = 'iOS'

    // Detect device type
    if (/Mobile|Android|iPhone|iPad|iPod/.test(ua))
      device = /iPad|Tablet/.test(ua) ? 'Tablet' : 'Mobile'

    return { browser, os, device }
  }

  /**
   * Create a new system log
   * Uses server API endpoint to bypass RLS policies
   * Supports both authenticated and anonymous users
   */
  const createLog = async (params: CreateLogParams): Promise<string | null> => {
    try {
      const { browser, os, device } = getBrowserInfo()

      const environment = import.meta.env.MODE || process.env.NODE_ENV || 'development'

      const currentUrl
        = typeof window !== 'undefined' ? window.location.href : undefined

      const userAgent
        = typeof navigator !== 'undefined' ? navigator.userAgent : undefined

      const referrer
        = typeof document !== 'undefined' ? document.referrer : undefined

      const userId = authStore.userId || getUserId(user.value)
      const userEmail = authStore.userEmail || user.value?.email || null

      const logData = {
        level: params.level,
        category: params.category,
        message: params.message,
        url: currentUrl,
        details: {
          ...(params.details || {}),
          url: currentUrl,
          referrer,
        },
        error: params.error
          ? {
            name: params.error.name,
            message: params.error.message,
            stack: params.error.stack,
          }
          : null,
        user_id: userId,
        user_email: userEmail,
        metadata: {
          ...(params.metadata || {}),
          browser,
          os,
          device,
          user_agent: userAgent,
          environment,
        },
      }

      // Use server API endpoint to create log (bypasses RLS)
      const response = await $fetch<{ success?: boolean, id?: string }>(
        '/api/logs/system',
        {
          method: 'POST',
          body: logData,
        },
      )

      if (response && response.success && response.id)
        return response.id

      return null
    }
    catch (err) {
      // Silently fail to prevent infinite loops
      // Only log to console if it's not a logging-related error
      const errorMessage = err instanceof Error ? err.message : String(err)
      if (!errorMessage.includes('vzw_logs_system'))
        console.warn('[useSupabaseLogs] Failed to create log:', errorMessage)

      return null
    }
  }

  /**
   * Fetch logs with filters and pagination
   */
  const fetchLogs = async (
    filters: LogFilters = {},
    page = 1,
    perPage = 50,
  ): Promise<{ data: SystemLog[], count: number } | null> => {
    loading.value = true
    error.value = null

    try {
      let query = supabase
        .from('vzw_logs_system')
        .select('*', { count: 'exact' })

      // Apply filters
      if (filters.level)
        query = query.eq('level', filters.level)

      if (filters.category)
        query = query.eq('category', filters.category)

      if (filters.is_resolved !== undefined)
        query = query.eq('is_resolved', filters.is_resolved)

      if (filters.user_id)
        query = query.eq('user_id', filters.user_id)

      if (filters.search) {
        query = query.or(
          `message.ilike.%${filters.search}%,error_message.ilike.%${filters.search}%`,
        )
      }

      if (filters.start_date)
        query = query.gte('created_at', filters.start_date)

      if (filters.end_date)
        query = query.lte('created_at', filters.end_date)

      // Pagination
      const from = (page - 1) * perPage
      const to = from + perPage - 1

      query = query.range(from, to).order('created_at', { ascending: false })

      const { data, error: fetchError, count } = await query

      if (fetchError) {
        error.value = fetchError

        return null
      }

      return { data: data as SystemLog[], count: count || 0 }
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return null
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Fetch a single log by ID
   */
  const fetchLogById = async (id: string): Promise<SystemLog | null> => {
    loading.value = true
    error.value = null

    try {
      const { data, error: fetchError } = await supabase
        .from('vzw_logs_system')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) {
        error.value = fetchError

        return null
      }

      return data as SystemLog
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return null
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Mark a log as resolved
   */
  const resolveLog = async (
    logId: string,
    resolutionNotes?: string,
  ): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const updateData: SystemLogUpdate = {
        is_resolved: true,
        resolved_by: getUserId(user.value),
        resolved_at: new Date().toISOString(),
        resolution_notes: resolutionNotes || null,
      }

      const { error: updateError } = await supabase
        .from('vzw_logs_system')
        .update(updateData)
        .eq('id', logId)

      if (updateError) {
        error.value = updateError

        return false
      }

      return true
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return false
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Mark multiple logs as resolved
   */
  const resolveMultipleLogs = async (
    logIds: string[],
    resolutionNotes?: string,
  ): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const updateData: SystemLogUpdate = {
        is_resolved: true,
        resolved_by: getUserId(user.value),
        resolved_at: new Date().toISOString(),
        resolution_notes: resolutionNotes || null,
      }

      const { error: updateError } = await supabase
        .from('vzw_logs_system')
        .update(updateData)
        .in('id', logIds)

      if (updateError) {
        error.value = updateError

        return false
      }

      return true
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return false
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Delete a log
   */
  const deleteLog = async (logId: string): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const { error: deleteError } = await supabase
        .from('vzw_logs_system')
        .delete()
        .eq('id', logId)

      if (deleteError) {
        error.value = deleteError

        return false
      }

      return true
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return false
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Delete multiple logs
   */
  const deleteMultipleLogs = async (logIds: string[]): Promise<boolean> => {
    loading.value = true
    error.value = null

    try {
      const { error: deleteError } = await supabase
        .from('vzw_logs_system')
        .delete()
        .in('id', logIds)

      if (deleteError) {
        error.value = deleteError

        return false
      }

      return true
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return false
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Get log statistics
   */
  const fetchLogStatistics = async (
    startDate?: string,
    endDate?: string,
  ): Promise<LogStatistics | null> => {
    loading.value = true
    error.value = null

    try {
      let query = supabase
        .from('vzw_logs_system')
        .select('level, category, is_resolved')

      if (startDate)
        query = query.gte('created_at', startDate)

      if (endDate)
        query = query.lte('created_at', endDate)

      const { data, error: fetchError } = await query

      if (fetchError) {
        error.value = fetchError

        return null
      }

      // Calculate statistics
      const stats: LogStatistics = {
        total_logs: data.length,
        error_count: data.filter(l => l.level === 'error').length,
        warning_count: data.filter(l => l.level === 'warning').length,
        info_count: data.filter(l => l.level === 'info').length,
        resolved_count: data.filter(l => l.is_resolved).length,
        unresolved_count: data.filter(l => !l.is_resolved).length,
        by_category: {},
      }

      // Count by category
      data.forEach(log => {
        stats.by_category[log.category]
          = (stats.by_category[log.category] || 0) + 1
      })

      return stats
    }
    catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))

      return null
    }
    finally {
      loading.value = false
    }
  }

  /**
   * Convenience method to log an error
   */
  const logError = (
    message: string,
    error?: Error,
    details?: Record<string, unknown>,
  ) => {
    return createLog({
      level: 'error',
      category: 'page_error',
      message,
      error,
      details,
    })
  }

  /**
   * Convenience method to log an authentication failure
   */
  const logAuthFailure = (message: string, details?: Record<string, unknown>) => {
    return createLog({
      level: 'error',
      category: 'authentication',
      message,
      details,
    })
  }

  /**
   * Convenience method to log a console error
   */
  const logConsoleError = (message: string, error?: Error) => {
    return createLog({
      level: 'error',
      category: 'console_error',
      message,
      error,
    })
  }

  /**
   * Convenience method to log an API error
   */
  const logApiError = (message: string, details?: Record<string, unknown>) => {
    return createLog({
      level: 'error',
      category: 'api_error',
      message,
      details,
    })
  }

  return {
    loading,
    error,
    createLog,
    fetchLogs,
    fetchLogById,
    resolveLog,
    resolveMultipleLogs,
    deleteLog,
    deleteMultipleLogs,
    fetchLogStatistics,
    logError,
    logAuthFailure,
    logConsoleError,
    logApiError,
  }
}
